<template>
  <div class="h-full overflow-auto">
    <div class="mobile-status-padding"></div>

    <!-- Back button + Title (same style as DealsPage header) -->
    <div class="py-4 flex items-center gap-3 px-6">
      <button
        @click="goBack"
        class="p-2 -ml-2 rounded-full hover:bg-slate-100 transition-colors"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-6 w-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M10 19l-7-7m0 0l7-7m-7 7h18"
          />
        </svg>
      </button>
      <div class="font-bold text-3xl title-color flex-1">Détails de l'offre</div>
    </div>

    <!-- Description -->
    <div class="px-6 text-sm text-slate-500">
      Voici tous les plats concernés par cette promotion.
    </div>

    <div class="py-4"></div>

    <!-- Deal Banner Card -->
    <div v-if="deal" class="mx-6">
      <div
        class="bg-white rounded-[2rem] overflow-hidden shadow-sm border border-slate-100 relative min-h-[140px]"
      >
        <!-- Decorative background -->
        <div
          class="absolute -skew-x-12 h-full w-2/3 right-0 translate-x-1/4 opacity-10 bg-primary"
        ></div>

        <div class="grid grid-cols-2 w-full h-full relative p-6">
          <!-- Left: discount info -->
          <div class="flex flex-col justify-center">
            <div class="flex items-baseline gap-1 text-primary flex-wrap">
              <template v-if="deal.discountType === 'percentage' || deal.discountType === 'percent'">
                <span class="text-4xl font-bold">-{{ deal.discountValue || deal.discount }}</span>
                <span class="text-xl font-medium">%</span>
              </template>
              <template v-else-if="deal.discountType === 'fixed'">
                <span class="text-4xl font-bold">-{{ deal.discountValue }}</span>
                <span class="text-xl font-medium">F</span>
              </template>
              <span class="text-xs opacity-70 font-medium ml-1 uppercase truncate">Réduction</span>
            </div>
            <div class="text-lg font-bold text-slate-800 leading-tight mt-1 line-clamp-2">
              {{ dealTitle }}
            </div>
            <div class="text-xs text-slate-400 mt-1 flex items-center gap-1">
              <span
                v-if="deal.restaurantLogo || deal.restaurant?.logo"
                class="w-4 h-4 rounded-full overflow-hidden inline-block shrink-0"
              >
                <img :src="deal.restaurantLogo || deal.restaurant?.logo" class="w-full h-full object-cover" />
              </span>
              {{ deal.restaurantName || deal.restaurant?.storeName || 'Restaurant partenaire' }}
            </div>
          </div>

          <!-- Right: deal image -->
          <div class="flex items-center justify-end">
            <div class="w-24 h-24 rounded-2xl overflow-hidden shadow-md border-4 border-white">
              <img :src="dealImage" class="w-full h-full object-cover bg-white" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="py-4"></div>

    <!-- Loading State -->
    <div v-if="loading" class="flex flex-col items-center justify-center py-20">
      <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
      <div class="mt-4 text-slate-400 font-medium">Chargement des plats...</div>
    </div>

    <!-- No Dishes -->
    <div
      v-else-if="dishes.length === 0"
      class="flex flex-col items-center justify-center py-20 px-10 text-center"
    >
      <div class="bg-slate-100 p-6 rounded-full">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-12 w-12 text-slate-300"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
      </div>
      <div class="mt-6 font-bold text-slate-600">Aucun plat disponible</div>
      <div class="mt-2 text-slate-400">
        Cette offre n'a pas encore de produits associés.
      </div>
    </div>

    <!-- Dishes Grid -->
    <div v-else class="px-6">
      <div class="font-bold text-lg text-slate-700 mb-4">
        {{ dishes.length }} Plat{{ dishes.length > 1 ? 's' : '' }}
        impacté{{ dishes.length > 1 ? 's' : '' }}
      </div>
      <div class="grid grid-cols-1 gap-6">
        <div v-for="dish in dishes" :key="dish.id" class="h-[280px]">
          <DishCard :item="dish" />
        </div>
      </div>
    </div>

    <div class="py-4"></div>
  </div>
</template>

<script setup>
import { computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { bus } from '@core/events/eventSystem';
import { getStore } from '@core/store';
import constants from '@core/utils/constants';
import DishCard from '../../home/components/DishCard.vue';

const props = defineProps({
  dealId: {
    type: String,
    default: null
  }
});

const emit = defineEmits(['close']);

const route = useRoute();
const router = useRouter();
const store = getStore('deals');

const deal = computed(() => store.state.currentDeal);
const dishes = computed(() => store.state.currentDealDishes);
const loading = computed(() => store.state.loading || store.state.dishesLoading);

const dealTitle = computed(() => {
  if (deal.value?.title) return deal.value.title;

  const type = deal.value?.targetType;
  const targetData = deal.value?.targetData || [];
  
  if (type === 'dish') {
    if (targetData.length === 1) {
      return `Offre sur ${targetData[0].name}`;
    } else if (targetData.length == 2) {
      return `Offre sur ${targetData[0].name} et ${targetData[1].name}`;
    } else if (targetData.length > 2) {
      return `Offre sur ${targetData[0].name} et ${targetData.length -1} autres plats`;
    }
    return 'Offre sur des plats sélectionnés';
  } else if (type === 'category') {
    if (targetData.length === 1) {
      return `Menu ${targetData[0].name}`;
    } else if (targetData.length == 2) {
      return `Menu ${targetData[0].name} et ${targetData[1].name}`;
    } else if (targetData.length > 2) {
      return `Menu ${targetData[0].name} et ${targetData.length -1} autres catégories`;
    }
    return 'Offre spéciale sur menu';
  } else if (type === 'delivery') {
    return 'Réduction sur la livraison';
  }
  
  return 'Offre spéciale';
});

const dealImage = computed(() => {
  return deal.value?.image || 
         deal.value?.principalImageUrl || 
         deal.value?.targetData?.[0]?.principalImageUrl || 
         deal.value?.targetData?.[0]?.imageUrl || 
         deal.value?.restaurantLogo || 
         deal.value?.restaurant?.logo || 
         '/images/logo.png';
});

onMounted(() => {
  const dealId = props.dealId || route.params.id;
  if (dealId) {
    bus.emit(constants.EVENTS.DEALS.LOAD_DETAIL, dealId);
  }
});

const goBack = () => {
  if (props.dealId) {
    emit('close');
  } else {
    router.back();
  }
};
</script>

<style scoped>
.text-primary {
  color: #f59e0b;
}
.bg-primary {
  background-color: #f59e0b;
}
.border-primary {
  border-color: #f59e0b;
}
.title-color {
  color: #1e293b;
}
</style>
